import type { ArticleFAQ } from '@/lib/articles';
import { opendoorUrl } from '@/lib/urls';

export const HOME_VALUE_PATH = '/home-value-estimator';

// Nested under HOME_VALUE_PATH so a single Cloudflare carve-out
// (`/home-value-estimator` + `/home-value-estimator/*` -> beacon) routes the
// page and its API together, without a global `/api/*` rule that would collide
// with cosmos.
export const HOME_VALUE_API_PATH = '/home-value-estimator/api/estimate';

// Gates both the robots meta (page.tsx) and the sitemap entry. Routed on
// www.opendoor.com via the Cloudflare Worker since 2026-06-11 (GROWTH-1868).
export const HOME_VALUE_INDEXABLE = true;

export const homeValueMeta = {
  title: 'Home Value Estimator: What Is My House Worth?',
  description:
    'Get a free, instant home value estimate from Opendoor. Enter your address to see an estimated value range based on real-time market data and comparable sales - no obligation.',
  keywords: [
    'home value',
    'home value estimator',
    'how much is my home worth',
    'what is my house worth',
    'house value estimator',
    'free home valuation',
  ],
};

export const homeValueHero = {
  heading: 'What is my home worth?',
  subheading:
    'Get a free, instant estimate of your home’s value based on real-time market data and recent comparable sales. Enter your address to see your range - no account or obligation required.',
  placeholder: 'Enter your home address',
};

export interface HowItWorksStep {
  title: string;
  description: string;
}

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    title: 'We analyze millions of data points',
    description:
      'Public records, recent home sales, listing activity, price trends, and property characteristics.',
  },
  {
    title: 'We compare similar nearby homes',
    description: 'Your home is matched with recently sold comparable properties in your area.',
  },
  {
    title: 'We adjust for market conditions',
    description: 'Local demand, seasonality, and pricing momentum help refine the estimate.',
  },
  {
    title: 'We update continuously',
    description: 'Estimates refresh regularly to reflect changes in the market.',
  },
];

export interface TrustStat {
  headline: string;
  detail: string;
}

export const TRUST_STATS: TrustStat[] = [
  { headline: 'Within 10%', detail: 'Most estimates land within 10% of the final sale price.' },
  {
    headline: '95% accuracy',
    detail: 'Over 95% of estimates fall within 20% of the actual sale price.',
  },
  {
    headline: 'Always current',
    detail: 'Estimates refresh regularly using the latest market data.',
  },
];

export interface ComparisonColumn {
  title: string;
  rows: string[];
  highlighted?: boolean;
}

export const COMPARISON_COLUMNS: ComparisonColumn[] = [
  {
    title: 'Opendoor Home Value Estimator',
    highlighted: true,
    rows: [
      'Free and instant',
      'Automated valuation using market data and comparable sales',
      'Best for early planning and price ranges',
    ],
  },
  {
    title: 'Professional appraisal',
    rows: [
      'Conducted by a licensed appraiser',
      'Includes an in-person inspection',
      'Best for financing, legal, or tax needs',
    ],
  },
  {
    title: 'Agent CMA',
    rows: [
      'Created by a local real estate agent',
      'Combines recent sales with local expertise',
      'Best for preparing to list',
    ],
  },
];

export interface HelpfulResource {
  title: string;
  description: string;
  href: string;
}

export const HELPFUL_RESOURCES: HelpfulResource[] = [
  {
    title: 'When is the best time to sell a house?',
    description: 'How seasonality and local demand affect your sale price and timeline.',
    href: '/articles/best-time-to-sell-a-house',
  },
  {
    title: 'Get a cash offer',
    description: 'Skip the listing process and get a no-obligation offer from Opendoor.',
    href: opendoorUrl('/sell'),
  },
];

export const HOME_VALUE_FAQS: ArticleFAQ[] = [
  {
    question: 'How does Opendoor calculate home values?',
    answer:
      'Opendoor uses models that analyze millions of data points including public records, recent home sales, listing activity, price trends, and property characteristics. We compare your home to recently sold comparable properties in your area and adjust for current market conditions to produce an estimate.',
  },
  {
    question: 'How accurate is the home value estimate?',
    answer:
      'Most Opendoor estimates are within 10% of the final sale price, and over 95% fall within 20% of the actual sale price. Accuracy varies based on market activity, data availability, and unique property features, and improves when there is more recent comparable sales data in your area.',
  },
  {
    question: 'How often are estimates updated?',
    answer:
      'Home value estimates refresh regularly using the latest market data. As new sales occur, listings change, and market conditions shift, the estimate is updated to stay current.',
  },
  {
    question: 'Why did my estimate change?',
    answer:
      'Estimates can change due to new comparable sales nearby, shifting local market conditions, seasonal trends, updated tax assessments, or new listing activity. These updates keep the estimate aligned with current market reality.',
  },
  {
    question: 'What data sources are used?',
    answer:
      'We use public property records, MLS data, recent sale transactions, tax assessments, property characteristics, local market trends, and economic indicators. Combining these sources helps keep estimates accurate across different markets and property types.',
  },
  {
    question: 'Does the estimate include renovations?',
    answer:
      'The initial estimate is based on public records and comparable sales. Significant renovations that are not yet reflected in public records may not be fully captured. For a valuation that accounts for recent improvements, you can request a detailed assessment by starting the selling process with Opendoor.',
  },
  {
    question: 'Can I update my home details?',
    answer:
      'To get a more personalized valuation that accounts for specific features, upgrades, or condition, start the selling process with Opendoor. You can then provide details about your home that are used to refine the estimate.',
  },
  {
    question: 'Is this the same as an appraisal?',
    answer:
      'No. This is an automated valuation model (AVM), not a professional appraisal. An appraisal is performed by a licensed appraiser who inspects your property in person. Our estimate is designed for planning and to give you a quick sense of your home’s value. Lenders typically require a professional appraisal for mortgage purposes.',
  },
  {
    question: 'Does using the estimator affect my credit?',
    answer:
      'No. Using the home value estimator has no impact on your credit score. There is no credit check, and the tool is free with no obligation. You can check your home’s value as often as you’d like.',
  },
  {
    question: 'Is the estimate free?',
    answer:
      'Yes. Getting a home value estimate from Opendoor is completely free with no obligation. There are no hidden fees, and you are not required to work with Opendoor after receiving your estimate.',
  },
  {
    question: 'What factors affect a home’s value the most?',
    answer:
      'The biggest factors are location (neighborhood, school district, and proximity to amenities), size (square footage and number of bedrooms and bathrooms), condition and age, recent comparable sales, local supply and demand, and broader economic factors like interest rates. Unique features and recent upgrades can also move value meaningfully.',
  },
  {
    question: 'How can I increase my home’s value?',
    answer:
      'Focus on high-impact improvements: updating kitchens and bathrooms, improving curb appeal, fresh interior and exterior paint, replacing dated fixtures, keeping core systems (HVAC, plumbing, electrical) in good shape, and addressing deferred maintenance. Targeted cosmetic updates often deliver the best return.',
  },
  {
    question: 'What is the difference between market value and appraised value?',
    answer:
      'Market value is what a willing buyer would pay a willing seller today - essentially what your home would likely sell for. Appraised value is a licensed appraiser’s professional opinion based on an in-person inspection and comparable sales. Lenders typically require an appraisal for financing.',
  },
  {
    question: 'Are online home value estimates reliable?',
    answer:
      'Online estimates are a useful starting point and are generally accurate for typical homes in areas with strong sales data. They can’t fully account for unique features, condition, or renovations not in public records, so for major decisions consider a professional appraisal or a detailed assessment.',
  },
  {
    question: 'When should I get a professional appraisal?',
    answer:
      'Consider a professional appraisal when refinancing or taking out a home equity loan, settling an estate, going through a divorce, appealing property taxes, selling a unique property, or making major financial decisions based on your home’s value.',
  },
];
